var __defProp = Object.defineProperty;
var __defProps = Object.defineProperties;
var __getOwnPropDescs = Object.getOwnPropertyDescriptors;
var __getOwnPropSymbols = Object.getOwnPropertySymbols;
var __hasOwnProp = Object.prototype.hasOwnProperty;
var __propIsEnum = Object.prototype.propertyIsEnumerable;
var __defNormalProp = (obj, key, value) => key in obj ? __defProp(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __spreadValues = (a, b) => {
  for (var prop in b || (b = {}))
    if (__hasOwnProp.call(b, prop))
      __defNormalProp(a, prop, b[prop]);
  if (__getOwnPropSymbols)
    for (var prop of __getOwnPropSymbols(b)) {
      if (__propIsEnum.call(b, prop))
        __defNormalProp(a, prop, b[prop]);
    }
  return a;
};
var __spreadProps = (a, b) => __defProps(a, __getOwnPropDescs(b));
var _a;
import React, { useLayoutEffect, useEffect, useState, createContext, useRef, useCallback, isValidElement, Fragment as Fragment$1, useMemo, useContext } from "react";
import { createPortal } from "react-dom";
import { useFloating, offset, flip, shift, autoPlacement, arrow, hide, autoUpdate } from "@floating-ui/react-dom";
import { Transition } from "@headlessui/react";
var commonjsGlobal = typeof globalThis !== "undefined" ? globalThis : typeof window !== "undefined" ? window : typeof global !== "undefined" ? global : typeof self !== "undefined" ? self : {};
var FUNC_ERROR_TEXT = "Expected a function";
var NAN = 0 / 0;
var symbolTag = "[object Symbol]";
var reTrim = /^\s+|\s+$/g;
var reIsBadHex = /^[-+]0x[0-9a-f]+$/i;
var reIsBinary = /^0b[01]+$/i;
var reIsOctal = /^0o[0-7]+$/i;
var freeParseInt = parseInt;
var freeGlobal = typeof commonjsGlobal == "object" && commonjsGlobal && commonjsGlobal.Object === Object && commonjsGlobal;
var freeSelf = typeof self == "object" && self && self.Object === Object && self;
var root = freeGlobal || freeSelf || Function("return this")();
var objectProto = Object.prototype;
var objectToString = objectProto.toString;
var nativeMax = Math.max, nativeMin = Math.min;
var now = function() {
  return root.Date.now();
};
function debounce(func, wait, options) {
  var lastArgs, lastThis, maxWait, result, timerId, lastCallTime, lastInvokeTime = 0, leading = false, maxing = false, trailing = true;
  if (typeof func != "function") {
    throw new TypeError(FUNC_ERROR_TEXT);
  }
  wait = toNumber(wait) || 0;
  if (isObject(options)) {
    leading = !!options.leading;
    maxing = "maxWait" in options;
    maxWait = maxing ? nativeMax(toNumber(options.maxWait) || 0, wait) : maxWait;
    trailing = "trailing" in options ? !!options.trailing : trailing;
  }
  function invokeFunc(time) {
    var args = lastArgs, thisArg = lastThis;
    lastArgs = lastThis = void 0;
    lastInvokeTime = time;
    result = func.apply(thisArg, args);
    return result;
  }
  function leadingEdge(time) {
    lastInvokeTime = time;
    timerId = setTimeout(timerExpired, wait);
    return leading ? invokeFunc(time) : result;
  }
  function remainingWait(time) {
    var timeSinceLastCall = time - lastCallTime, timeSinceLastInvoke = time - lastInvokeTime, result2 = wait - timeSinceLastCall;
    return maxing ? nativeMin(result2, maxWait - timeSinceLastInvoke) : result2;
  }
  function shouldInvoke(time) {
    var timeSinceLastCall = time - lastCallTime, timeSinceLastInvoke = time - lastInvokeTime;
    return lastCallTime === void 0 || timeSinceLastCall >= wait || timeSinceLastCall < 0 || maxing && timeSinceLastInvoke >= maxWait;
  }
  function timerExpired() {
    var time = now();
    if (shouldInvoke(time)) {
      return trailingEdge(time);
    }
    timerId = setTimeout(timerExpired, remainingWait(time));
  }
  function trailingEdge(time) {
    timerId = void 0;
    if (trailing && lastArgs) {
      return invokeFunc(time);
    }
    lastArgs = lastThis = void 0;
    return result;
  }
  function cancel() {
    if (timerId !== void 0) {
      clearTimeout(timerId);
    }
    lastInvokeTime = 0;
    lastArgs = lastCallTime = lastThis = timerId = void 0;
  }
  function flush() {
    return timerId === void 0 ? result : trailingEdge(now());
  }
  function debounced() {
    var time = now(), isInvoking = shouldInvoke(time);
    lastArgs = arguments;
    lastThis = this;
    lastCallTime = time;
    if (isInvoking) {
      if (timerId === void 0) {
        return leadingEdge(lastCallTime);
      }
      if (maxing) {
        timerId = setTimeout(timerExpired, wait);
        return invokeFunc(lastCallTime);
      }
    }
    if (timerId === void 0) {
      timerId = setTimeout(timerExpired, wait);
    }
    return result;
  }
  debounced.cancel = cancel;
  debounced.flush = flush;
  return debounced;
}
function throttle(func, wait, options) {
  var leading = true, trailing = true;
  if (typeof func != "function") {
    throw new TypeError(FUNC_ERROR_TEXT);
  }
  if (isObject(options)) {
    leading = "leading" in options ? !!options.leading : leading;
    trailing = "trailing" in options ? !!options.trailing : trailing;
  }
  return debounce(func, wait, {
    "leading": leading,
    "maxWait": wait,
    "trailing": trailing
  });
}
function isObject(value) {
  var type = typeof value;
  return !!value && (type == "object" || type == "function");
}
function isObjectLike(value) {
  return !!value && typeof value == "object";
}
function isSymbol(value) {
  return typeof value == "symbol" || isObjectLike(value) && objectToString.call(value) == symbolTag;
}
function toNumber(value) {
  if (typeof value == "number") {
    return value;
  }
  if (isSymbol(value)) {
    return NAN;
  }
  if (isObject(value)) {
    var other = typeof value.valueOf == "function" ? value.valueOf() : value;
    value = isObject(other) ? other + "" : other;
  }
  if (typeof value != "string") {
    return value === 0 ? value : +value;
  }
  value = value.replace(reTrim, "");
  var isBinary = reIsBinary.test(value);
  return isBinary || reIsOctal.test(value) ? freeParseInt(value.slice(2), isBinary ? 2 : 8) : reIsBadHex.test(value) ? NAN : +value;
}
var lodash_throttle = throttle;
const tailwindcssOriginSafelist = [
  "origin-bottom",
  "origin-top",
  "origin-right",
  "origin-left",
  "origin-bottom-left",
  "origin-bottom-right",
  "origin-top-left",
  "origin-top-right"
];
const tailwindcssOriginClassResolver = (placement) => {
  switch (placement) {
    case "top":
      return "origin-bottom";
    case "bottom":
      return "origin-top";
    case "left":
      return "origin-right";
    case "right":
      return "origin-left";
    case "top-start":
    case "right-end":
      return "origin-bottom-left";
    case "top-end":
    case "left-end":
      return "origin-bottom-right";
    case "right-start":
    case "bottom-start":
      return "origin-top-left";
    case "left-start":
    case "bottom-end":
      return "origin-top-right";
    default:
      return "";
  }
};
const useIsoMorphicEffect = typeof window !== "undefined" ? useLayoutEffect : useEffect;
let state = { serverHandoffComplete: false };
function useServerHandoffComplete() {
  let [serverHandoffComplete, setServerHandoffComplete] = useState(state.serverHandoffComplete);
  useEffect(() => {
    if (serverHandoffComplete === true)
      return;
    setServerHandoffComplete(true);
  }, [serverHandoffComplete]);
  useEffect(() => {
    if (state.serverHandoffComplete === false)
      state.serverHandoffComplete = true;
  }, []);
  return serverHandoffComplete;
}
let id = 0;
function generateId() {
  return ++id;
}
let useId = (_a = React.useId) != null ? _a : function useId2() {
  let ready = useServerHandoffComplete();
  let [id2, setId] = React.useState(ready ? generateId : null);
  useIsoMorphicEffect(() => {
    if (id2 === null)
      setId(generateId());
  }, [id2]);
  return id2 != null ? "" + id2 : void 0;
};
var jsxRuntime = { exports: {} };
var reactJsxRuntime_production_min = {};
/**
 * @license React
 * react-jsx-runtime.production.min.js
 *
 * Copyright (c) Facebook, Inc. and its affiliates.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 */
var f = React, k = Symbol.for("react.element"), l = Symbol.for("react.fragment"), m = Object.prototype.hasOwnProperty, n = f.__SECRET_INTERNALS_DO_NOT_USE_OR_YOU_WILL_BE_FIRED.ReactCurrentOwner, p = { key: true, ref: true, __self: true, __source: true };
function q(c, a, g) {
  var b, d = {}, e = null, h = null;
  g !== void 0 && (e = "" + g);
  a.key !== void 0 && (e = "" + a.key);
  a.ref !== void 0 && (h = a.ref);
  for (b in a)
    m.call(a, b) && !p.hasOwnProperty(b) && (d[b] = a[b]);
  if (c && c.defaultProps)
    for (b in a = c.defaultProps, a)
      d[b] === void 0 && (d[b] = a[b]);
  return { $$typeof: k, type: c, key: e, ref: h, props: d, _owner: n.current };
}
reactJsxRuntime_production_min.Fragment = l;
reactJsxRuntime_production_min.jsx = q;
reactJsxRuntime_production_min.jsxs = q;
{
  jsxRuntime.exports = reactJsxRuntime_production_min;
}
const jsx = jsxRuntime.exports.jsx;
const jsxs = jsxRuntime.exports.jsxs;
const Fragment = jsxRuntime.exports.Fragment;
let autoUpdateCleanerMap = /* @__PURE__ */ new Map();
let showStateMap = /* @__PURE__ */ new Map();
const ArrowContext = createContext(null);
ArrowContext.displayName = "ArrowContext";
function useArrowContext(component) {
  let context = useContext(ArrowContext);
  if (context === null) {
    let err = new Error(`<${component} /> is missing a parent <Float /> component.`);
    if (Error.captureStackTrace)
      Error.captureStackTrace(err, useArrowContext);
    throw err;
  }
  return context;
}
function FloatRoot(props) {
  var _a2, _b;
  const id2 = useId();
  const [isMounted, setIsMounted] = useState(false);
  const [show, setShow] = useState(props.show !== void 0 ? props.show : false);
  const [middleware, setMiddleware] = useState();
  const arrowRef = useRef(null);
  const events = {
    show: props.onShow || (() => {
    }),
    hide: props.onHide || (() => {
    }),
    update: props.onUpdate || (() => {
    })
  };
  const {
    x,
    y,
    placement,
    strategy,
    reference,
    floating,
    update,
    refs,
    middlewareData
  } = useFloating({
    placement: props.placement || "bottom-start",
    strategy: props.strategy,
    middleware
  });
  const updateFloating = useCallback(() => {
    update();
    events.update();
  }, [update]);
  useEffect(() => {
    const _middleware = [];
    if (typeof props.offset === "number" || typeof props.offset === "object" || typeof props.offset === "function") {
      _middleware.push(offset(props.offset));
    }
    if (props.flip === true || typeof props.flip === "number" || typeof props.flip === "object") {
      _middleware.push(flip(__spreadValues({
        padding: typeof props.flip === "number" ? props.flip : void 0
      }, typeof props.flip === "object" ? props.flip : {})));
    }
    if (props.shift === true || typeof props.shift === "number" || typeof props.shift === "object") {
      _middleware.push(shift(__spreadValues({
        padding: typeof props.shift === "number" ? props.shift : void 0
      }, typeof props.shift === "object" ? props.shift : {})));
    }
    if (props.autoPlacement === true || typeof props.autoPlacement === "object") {
      _middleware.push(autoPlacement(typeof props.autoPlacement === "object" ? props.autoPlacement : void 0));
    }
    if (props.arrow === true || typeof props.arrow === "number") {
      _middleware.push(arrow({
        element: arrowRef,
        padding: props.arrow === true ? 0 : props.arrow
      }));
    }
    _middleware.push(...typeof props.middleware === "function" ? props.middleware({
      referenceEl: refs.reference,
      floatingEl: refs.floating
    }) : props.middleware || []);
    if (props.hide === true || typeof props.hide === "object") {
      _middleware.push(hide(typeof props.hide === "object" ? props.hide : void 0));
    }
    setMiddleware(_middleware);
  }, [props.offset, props.shift, props.flip, props.arrow, props.autoPlacement, props.hide, props.middleware]);
  const startAutoUpdate = () => {
    if (refs.reference.current && refs.floating.current && props.autoUpdate !== false && !autoUpdateCleanerMap.get(id2)) {
      autoUpdateCleanerMap.set(id2, autoUpdate(refs.reference.current, refs.floating.current, lodash_throttle(updateFloating, 16), typeof props.autoUpdate === "object" ? props.autoUpdate : void 0));
    }
  };
  const clearAutoUpdate = () => {
    const disposeAutoUpdate = autoUpdateCleanerMap.get(id2);
    disposeAutoUpdate();
    autoUpdateCleanerMap.delete(id2);
  };
  useIsoMorphicEffect(() => {
    if (refs.reference.current && refs.floating.current && show === true && !showStateMap.get(id2)) {
      showStateMap.set(id2, true);
      events.show();
      startAutoUpdate();
    } else if (show === false && showStateMap.get(id2) && autoUpdateCleanerMap.get(id2)) {
      showStateMap.delete(id2);
      clearAutoUpdate();
      events.hide();
    }
    return autoUpdateCleanerMap.get(id2);
  }, [show]);
  useEffect(() => {
    setIsMounted(true);
  }, []);
  const arrowApi = {
    arrowRef,
    placement,
    x: (_a2 = middlewareData.arrow) == null ? void 0 : _a2.x,
    y: (_b = middlewareData.arrow) == null ? void 0 : _b.y
  };
  const [ReferenceNode, FloatingNode] = props.children;
  if (!isValidElement(ReferenceNode)) {
    return /* @__PURE__ */ jsx(Fragment$1, {});
  }
  const originClassValue = useMemo(() => {
    if (typeof props.originClass === "function") {
      return props.originClass(placement);
    } else if (typeof props.originClass === "string") {
      return props.originClass;
    } else if (props.tailwindcssOriginClass) {
      return tailwindcssOriginClassResolver(placement);
    }
    return "";
  }, [props.originClass, props.tailwindcssOriginClass]);
  const transitionProps = {
    show: isMounted ? props.show : false,
    enter: `${props.enter || ""} ${originClassValue}`,
    enterFrom: `${props.enterFrom || ""}`,
    enterTo: `${props.enterTo || ""}`,
    leave: `${props.leave || ""} ${originClassValue}`,
    leaveFrom: `${props.leaveFrom || ""}`,
    leaveTo: `${props.leaveTo || ""}`,
    beforeEnter: () => {
      setShow(true);
    },
    afterLeave: () => {
      setShow(false);
    }
  };
  const floatingProps = {
    ref: floating,
    style: props.transform || props.transform === void 0 ? {
      position: strategy,
      zIndex: props.zIndex || 9999,
      top: 0,
      left: 0,
      right: "auto",
      bottom: "auto",
      transform: `translate(${Math.round(x || 0)}px,${Math.round(y || 0)}px)`
    } : {
      position: strategy,
      zIndex: props.zIndex || 9999,
      top: `${y || 0}px`,
      left: `${x || 0}px`
    }
  };
  const renderPortal = (children) => {
    if (isMounted && props.portal) {
      const root2 = document == null ? void 0 : document.querySelector(props.portal === true ? "body" : props.portal);
      if (root2) {
        return createPortal(children, root2);
      }
    }
    return children;
  };
  const renderFloating = (Children) => {
    if (props.as === Fragment$1) {
      return /* @__PURE__ */ jsx(Children.type, __spreadValues(__spreadValues({}, Children.props), floatingProps));
    }
    const FloatingWrapper = props.as || "div";
    return /* @__PURE__ */ jsx(FloatingWrapper, __spreadProps(__spreadValues({}, floatingProps), {
      children: /* @__PURE__ */ jsx(Children.type, __spreadValues({}, Children.props))
    }));
  };
  return /* @__PURE__ */ jsxs(Fragment, {
    children: [/* @__PURE__ */ jsx(ReferenceNode.type, __spreadProps(__spreadValues({}, ReferenceNode.props), {
      ref: reference
    })), /* @__PURE__ */ jsx(ArrowContext.Provider, {
      value: arrowApi,
      children: renderPortal(renderFloating(/* @__PURE__ */ jsx(Transition, __spreadProps(__spreadValues({
        as: Fragment$1
      }, transitionProps), {
        children: /* @__PURE__ */ jsx(FloatingNode.type, __spreadValues({}, FloatingNode.props))
      }))))
    })]
  });
}
function Arrow(props) {
  var _a2;
  const {
    arrowRef,
    placement,
    x,
    y
  } = useArrowContext("Float.Arrow");
  const staticSide = {
    top: "bottom",
    right: "left",
    bottom: "top",
    left: "right"
  }[placement.split("-")[0]];
  const style = {
    left: typeof x === "number" ? `${x}px` : "",
    top: typeof y === "number" ? `${y}px` : "",
    right: "",
    bottom: "",
    [staticSide]: `${((_a2 = props.offset) != null ? _a2 : 4) * -1}px`
  };
  if (props.as === Fragment$1) {
    const slot = {
      placement
    };
    const ArrowNode = typeof props.children === "function" ? props.children(slot) : props.children;
    if (!ArrowNode || !isValidElement(ArrowNode)) {
      throw new Error("When the prop `as` of <Float.Arrow /> is <Fragment />, there must be contains 1 child element.");
    }
    return /* @__PURE__ */ jsx(ArrowNode.type, __spreadProps(__spreadValues({}, ArrowNode.props), {
      ref: arrowRef,
      style
    }));
  }
  const ArrowWrapper = props.as || "div";
  return /* @__PURE__ */ jsx(ArrowWrapper, __spreadProps(__spreadValues({}, props), {
    ref: arrowRef,
    style
  }));
}
const Float = Object.assign(FloatRoot, {
  Arrow
});
export { Float, tailwindcssOriginClassResolver, tailwindcssOriginSafelist };
